import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CampaignSubscriberWithJourney } from '@/shared/types/campaigns';
import { parseTimestamp } from '@/util/dateUtils';

import { cleanContextString } from '../../utils';

interface CurrentStatusCardProps {
  subscriber: CampaignSubscriberWithJourney;
}

export const CurrentStatusCard = ({ subscriber }: CurrentStatusCardProps) => {
  const { t } = useTranslation();

  const debt = useMemo(() => {
    const ctx = subscriber.context as Record<string, unknown> | undefined;
    // Orchestrator PR #1839: `context.debts[]` (array) replaced `context.debt` (singular).
    const debts = ctx?.debts as
      Array<{ amount?: number; currency?: string; due_date?: number | string }> | undefined;
    return debts?.[0];
  }, [subscriber.context]);

  const daysPastDue = useMemo(() => {
    // parseTimestamp treats "YYYY-MM-DD" as local midnight, matching how
    // formatDate renders dates elsewhere in the app. Strings are cleaned so
    // placeholder tokens ("null", "undefined", etc.) become missing; numeric
    // Unix-ms timestamps pass through unchanged.
    const rawDueDate = debt?.due_date;
    const cleaned = typeof rawDueDate === 'number' ? rawDueDate : cleanContextString(rawDueDate);
    const dueDate = cleaned ? parseTimestamp(cleaned) : null;
    if (!dueDate) {
      return null;
    }
    const diffMs = Date.now() - dueDate.getTime();
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  }, [debt?.due_date]);

  const balance = useMemo(() => {
    if (!debt?.amount) {
      return null;
    }
    const currency = debt.currency || 'USD';
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(debt.amount);
  }, [debt]);

  return (
    <Card className="rounded-md bg-background pt-0 text-foreground shadow-none">
      <CardHeader className="flex items-center border-b pt-3 pb-2.5! pl-5">
        <CardTitle className="text-base leading-none">
          {t('campaigns.subscriber_journey.current_status')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex justify-between">
          <div className="flex flex-col gap-1">
            <InfoTooltip content={t('campaigns.subscriber_journey.days_past_due')}>
              <span className="w-fit cursor-default text-xs text-foreground-muted">
                {t('campaigns.subscriber_journey.days_past_due_short')}
              </span>
            </InfoTooltip>
            <span className="text-sm font-semibold">
              {daysPastDue !== null ? daysPastDue : t('common.na')}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-foreground-muted">
              {t('campaigns.subscriber_journey.balance')}
            </span>
            <span className="text-sm font-semibold">{balance || t('common.na')}</span>
          </div>
          {/* //TODO add promise to pay */}
        </div>
      </CardContent>
    </Card>
  );
};
